// Consolidated Control Bar component
// CREATED: Reusable component that combines search, filters, count, and sorting in a single line
// Used for consistent control bar layout across list pages (templates, users, sites, services, incidents, etc.)
// UPDATED: Added hasActiveFilters/onClearFilters props for reset button, wrapperClassName for outer container styling
// UPDATED: itemCount now accepts number | null to support loading state (shows skeleton when null)

import { ArrowUp, ArrowDown } from 'lucide-react';
import { SearchBar } from './SearchBar';
import { FilterDropdown } from './FilterDropdown';
import type { FilterOption } from './FilterDropdown';

interface ConsolidatedControlBarProps {
  // Search
  searchValue: string;
  onSearchChange: (value: string) => void;
  searchPlaceholder?: string;

  // Count
  itemCount: number | null;
  itemLabel: string; // e.g., "mission", "utilisateur", "site"

  // Filters (optional)
  filters?: Array<{
    label: string;
    options: FilterOption[];
    selectedValues: string[];
    onChange: (values: string[]) => void;
  }>;

  // Clear filters (optional)
  hasActiveFilters?: boolean;
  onClearFilters?: () => void;

  // Wrapper (optional)
  wrapperClassName?: string;

  // Sort (optional)
  showSort?: boolean;
  sortOrder?: 'asc' | 'desc';
  onSortOrderChange?: (order: 'asc' | 'desc') => void;
  sortLabel?: string; // e.g., "Tri: A-Z", defaults to "Tri"
}

export function ConsolidatedControlBar({
  searchValue,
  onSearchChange,
  searchPlaceholder = 'Rechercher...',
  itemCount,
  itemLabel,
  filters = [],
  hasActiveFilters = false,
  onClearFilters,
  wrapperClassName,
  showSort = true,
  sortOrder = 'asc',
  onSortOrderChange,
  sortLabel = 'Tri ',
}: ConsolidatedControlBarProps) {
  const content = (
    <div className="flex items-center gap-4 flex-wrap">
      {/* Search bar */}
      <div className="flex-1 min-w-[200px] max-w-md">
        <SearchBar
          placeholder={searchPlaceholder}
          value={searchValue}
          onChange={onSearchChange}
        />
      </div>

      {/* Item count */}
      <div className="text-sm text-gray-500 whitespace-nowrap">
        {itemCount !== null ? (
          <>{itemCount} {itemLabel}{itemCount > 1 ? 's' : ''}</>
        ) : (
          <span className="inline-block w-16 h-4 bg-gray-200 rounded animate-pulse align-middle" />
        )}
      </div>

      {/* Filters and Sort grouped together on the right */}
      <div className="flex items-center gap-4 ml-auto">
        {/* Filters */}
        {filters.map((filter) => (
          <FilterDropdown
            key={filter.label}
            label={filter.label}
            options={filter.options}
            selectedValues={filter.selectedValues}
            onChange={filter.onChange}
          />
        ))}

        {/* Clear filters button (optional) */}
        {hasActiveFilters && onClearFilters && (
          <button
            onClick={onClearFilters}
            className="text-sm text-primary-600 hover:text-primary-700 font-medium transition-colors whitespace-nowrap"
          >
            Réinitialiser les filtres
          </button>
        )}

        {/* Sort button (optional) */}
        {showSort && onSortOrderChange && (
          <button
            onClick={() => onSortOrderChange(sortOrder === 'asc' ? 'desc' : 'asc')}
            className="flex items-center gap-2 text-sm text-gray-700 hover:text-gray-900 font-medium transition-colors whitespace-nowrap"
          >
            <span>{sortLabel}: {sortOrder === 'asc' ? 'A-Z' : 'Z-A'}</span>
            {sortOrder === 'asc' ? (
              <ArrowUp size={16} className="text-primary-600" />
            ) : (
              <ArrowDown size={16} className="text-primary-600" />
            )}
          </button>
        )}
      </div>
    </div>
  );

  if (wrapperClassName) {
    return <div className={wrapperClassName}>{content}</div>;
  }

  return content;
}
